import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle, Shield, Info } from "lucide-react";

const TermsModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if user has already accepted terms
    const hasAccepted = localStorage.getItem('terms-accepted');
    if (!hasAccepted) {
      setIsOpen(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('terms-accepted', 'true');
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold">
            <Shield className="h-5 w-5 text-primary" />
            Important Legal Disclaimers & Terms of Use
          </DialogTitle>
          <DialogDescription className="text-base">
            Please read the following important information before continuing to use this website.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Financial Advice Disclaimer */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h3 className="font-semibold text-base">Financial Advice Disclaimer</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The information contained on this website is for information purposes only and does not constitute 
              financial advice, investment advice, trading advice, or any other sort of advice. You should not treat 
              any of the website's content as such. We do not recommend that any investment decision be made based 
              on the information presented on this website.
            </p>
          </div>

          <Separator />

          {/* US Residents Restriction */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-500" />
              <h3 className="font-semibold text-base">Geographic Restrictions</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong>By continuing to use this website, you confirm that you are NOT a resident or citizen 
              of the United States of America.</strong> The services and information provided on this website 
              are not intended for, and must not be accessed by or distributed or disseminated to, persons 
              resident or located in the United States.
            </p>
          </div>

          <Separator />

          {/* Investment Risks */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <h3 className="font-semibold text-base">Investment Risk Warning</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              All investments carry risk and may lose value. Past performance is not a guide to future performance 
              and may not be repeated. The value of investments and any income from them may go down as well as up 
              and you may not get back the amount originally invested. Currency fluctuations may affect the value 
              of overseas investments.
            </p>
          </div>

          <Separator />

          {/* Professional Advice */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-base">Professional Advice Recommendation</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Before making any investment decisions, you should seek professional financial advice that takes 
              into account your individual circumstances, financial situation, and investment objectives. 
              Tax treatment depends on individual circumstances and may be subject to change.
            </p>
          </div>

          <Separator />

          {/* Regulatory Information */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-base">Regulatory Status</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Ascot Wealth Management Ltd. (trading as Cape Berkshire Asset Management) is authorised and regulated by the Financial Conduct Authority (FCA). 
              This website is intended for UK residents only. If you are unsure about the suitability 
              of any investment, you should seek professional advice.
            </p>
          </div>

          <Separator />

          {/* Terms Acceptance */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <p className="text-sm font-medium text-center">
              By clicking "Accept and Continue" below, you acknowledge that you have read, understood, 
              and agree to be bound by these terms and disclaimers, and confirm that you are not a 
              US resident or citizen.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleAccept} className="w-full" size="lg">
            Accept and Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TermsModal;